import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  clearHeldCredentials,
  mergeHeldParams,
  readHeldCredentials,
  stripSensitiveParamsFromAddressBar,
} from '@/utils/sensitiveUrl';

/**
 * For auth routes whose URL carries a one-time credential (token, code, email).
 *
 * Layer 1 (index.html, before GTM): on a full page load of an auth route the
 * credential is moved off the URL and HELD for this tab before any third-party
 * tag runs. This hook merges it back, for this route only.
 *
 * Layer 2 (this hook): if the credential is still in the URL (an in-app
 * navigation, or a cached old index.html), it is stripped from the address bar
 * in a layout effect, before any passive analytics effect. history.replaceState
 * does not notify React Router, so the router keeps the params it read.
 *
 * The held value is read once per mount and cleared when the page unmounts, so
 * a later visit to the same route in this tab does not reuse a spent link.
 * A child of the page that owns the route passes { clearOnUnmount: false }.
 */
const useCapturedUrlParams = ({ clearOnUnmount = true } = {}) => {
  const [routerParams] = useSearchParams();
  const { pathname } = useLocation();
  const [held] = useState(() => readHeldCredentials(pathname));

  const params = useMemo(() => mergeHeldParams(routerParams, held), [routerParams, held]);

  useLayoutEffect(() => {
    stripSensitiveParamsFromAddressBar();
  }, []);

  useEffect(
    () => () => {
      if (clearOnUnmount) clearHeldCredentials(pathname);
    },
    [pathname, clearOnUnmount]
  );

  return params;
};

export default useCapturedUrlParams;
